import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Avatar,
  Chip,
  Divider,
  Autocomplete,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import Header from '../../components/Layout/Header';
import Sidebar from '../../components/Layout/Sidebar';
import {
  classService,
  studentService,
  feeCategoryService,
  feeClassFeeService,
  feeStudentFeeService,
} from '../../services/apiService';
import { showToast } from '../../utils/toast';
import { getAvatarColor, getInitials } from '../../utils/avatar';

const MONTH_OPTIONS = [
  { value: '', label: '—' },
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const defaultRowState = {
  id: '',
  status: 'opted_out',
  base_amount: 0,
  amount_override: '',
  effective_start_month: 4,
  effective_end_month: 3,
  notes: '',
};

const AssignStudentFeesPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const drawerWidth = 280;

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleDrawerToggle = () => {
    setSidebarOpen((prev) => !prev);
  };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [classes, setClasses] = useState([]);
  const [studentsByClass, setStudentsByClass] = useState({});
  const [classAssignments, setClassAssignments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [rowState, setRowState] = useState({});
  const [savingRow, setSavingRow] = useState(null);

  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError('');
      const [classRes, categoryRes] = await Promise.all([
        classService.getAll(),
        feeCategoryService.getAll(),
      ]);
      if (!classRes?.success) {
        throw new Error(classRes?.error || 'Failed to load classes');
      }
      if (!categoryRes?.success) {
        throw new Error(categoryRes?.error || 'Failed to load categories');
      }
      setClasses(classRes.data || []);
      const filteredCategories = (categoryRes.data || []).filter(
        (cat) => cat.charge_scope === 'student',
      );
      setCategories(filteredCategories);
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Unable to load data';
      setError(message);
      showToast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  const ensureStudentsLoaded = async (classId) => {
    if (!classId || studentsByClass[classId]) return;
    try {
      const response = await studentService.getAll({ class_id: classId });
      if (!response?.success) {
        throw new Error(response?.error || 'Failed to load students');
      }
      setStudentsByClass((prev) => ({
        ...prev,
        [classId]: response.data || [],
      }));
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Unable to load students';
      showToast.error(message);
    }
  };

  const loadAssignments = async (classId, studentId) => {
    if (!classId || !studentId) {
      setAssignments([]);
      return;
    }
    try {
      setLoading(true);
      const response = await feeStudentFeeService.getAll({
        class_id: classId,
        student_id: studentId,
      });
      if (!response?.success) {
        throw new Error(response?.error || 'Failed to load student assignments');
      }
      setAssignments(response.data || []);
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Unable to load student fees';
      setError(message);
      showToast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const loadClassAssignments = async (classId) => {
    if (!classId) {
      setClassAssignments([]);
      return;
    }
    try {
      const response = await feeClassFeeService.getAll({ class_id: classId });
      if (!response?.success) {
        throw new Error(response?.error || 'Failed to load class fee assignments');
      }
      setClassAssignments(response.data || []);
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Unable to load class fee assignments';
      showToast.error(message);
    }
  };

  useEffect(() => {
    if (selectedClass && selectedStudent) {
      loadAssignments(selectedClass, selectedStudent);
    } else {
      setAssignments([]);
    }
  }, [selectedClass, selectedStudent]);

  useEffect(() => {
    if (selectedClass) {
      loadClassAssignments(selectedClass);
    } else {
      setClassAssignments([]);
    }
  }, [selectedClass]);

  useEffect(() => {
    setRowState((prev) => {
      const nextState = {};
      const classAssignmentsMap = classAssignments.reduce((acc, item) => {
        acc[item.fee_category_id] = item;
        return acc;
      }, {});
      
      // Get current month (1-12)
      const currentMonth = new Date().getMonth() + 1;
      
      categories.forEach((cat) => {
        const assignment = assignments.find((item) => item.fee_category_id === cat.id);
        const classAssignment = classAssignmentsMap[cat.id];
        const baseAmount = classAssignment
          ? Number(classAssignment.amount ?? classAssignment.default_amount ?? 0)
          : Number(cat.default_amount ?? 0);
        const previous = prev[cat.id] || defaultRowState;
        const defaultStart = classAssignment?.start_month ?? 4;
        const defaultEnd = classAssignment?.end_month ?? 3;
        
        // If assignment exists, use its status; otherwise, use default based on whether fee is optional
        // Don't use previous.status when assignment doesn't exist (student hasn't opted in)
        // For optional fees, default to 'opted_out'; for required fees, default to 'opted_in'
        const defaultStatus = 'opted_out';
        
        // Get effective end month from assignment or default
        const effectiveEndMonth = assignment?.effective_end_month !== undefined && assignment?.effective_end_month !== null
          ? assignment.effective_end_month
          : (assignment?.id ? defaultEnd : null);
        
        // Check if end month has passed - if current month > end month, automatically opt out
        let finalStatus = assignment?.status && assignment.status !== '' 
          ? assignment.status 
          : defaultStatus;
        
        // Auto opt-out if end month has passed (only if end month is set and assignment exists)
        if (assignment?.id && effectiveEndMonth !== null && effectiveEndMonth !== '' && Number(effectiveEndMonth) > 0) {
          if (currentMonth > Number(effectiveEndMonth)) {
            finalStatus = 'opted_out';
            // Auto-update the status in the backend if it's currently opted_in
            if (assignment.status === 'opted_in') {
              // Update in background without blocking UI
              feeStudentFeeService.update(assignment.id, { status: 'opted_out' }).catch(err => {
                console.error('Failed to auto-update opt-out status:', err);
              });
            }
          }
        }
        
        nextState[cat.id] = {
          id: assignment?.id || '',
          status: finalStatus,
          base_amount: baseAmount,
          amount_override:
            assignment?.amount_override !== undefined && assignment?.amount_override !== null
              ? assignment.amount_override
              : (assignment?.id ? baseAmount : ''),
          effective_start_month:
            assignment?.effective_start_month !== undefined && assignment?.effective_start_month !== null
              ? assignment.effective_start_month
              : (assignment?.id ? defaultStart : ''),
          effective_end_month:
            assignment?.effective_end_month !== undefined && assignment?.effective_end_month !== null
              ? assignment.effective_end_month
              : (assignment?.id ? defaultEnd : ''),
          notes: assignment?.notes || '',
        };
      });
      return nextState;
    });
  }, [categories, assignments, classAssignments]);

  const studentOptions = useMemo(() => studentsByClass[selectedClass] || [], [studentsByClass, selectedClass]);


  const handleClassChange = async (value) => {
    setSelectedClass(value);
    setSelectedStudent('');
    setAssignments([]);
    setRowState({});
    if (value) {
      await ensureStudentsLoaded(value);
    }
  };

  const handleRowChange = (categoryId, field) => (event) => {
    let { value } = event.target;
    if (field === 'effective_start_month' || field === 'effective_end_month') {
      value = value === '' ? '' : Number(value);
    }
    setRowState((prev) => ({
      ...prev,
      [categoryId]: {
        ...(prev[categoryId] || defaultRowState),
        [field]: value,
      },
    }));
  };

  const handleSaveRow = async (category) => {
    if (!selectedClass || !selectedStudent) {
      showToast.error('Select class and student first');
      return;
    }
    const row = rowState[category.id] || defaultRowState;
    if (!row.id && row.status === 'opted_out') {
      showToast.error('Choose "Opted In" before saving');
      return;
    }

    // Check if end month has passed - prevent opting in if expired
    const currentMonth = new Date().getMonth() + 1;
    const effectiveEndMonth = row.effective_end_month !== '' && row.effective_end_month !== null 
      ? Number(row.effective_end_month) 
      : null;
    
    if (row.status === 'opted_in' && effectiveEndMonth !== null && effectiveEndMonth > 0) {
      if (currentMonth > effectiveEndMonth) {
        showToast.error('Cannot opt in: The end month has already passed. Please update the end month first.');
        return;
      }
    }

    const payload = {
      student_id: selectedStudent,
      class_id: selectedClass,
      fee_category_id: category.id,
      status: row.status,
      amount_override: row.amount_override !== '' ? Number(row.amount_override) : Number(row.base_amount || 0),
      effective_start_month: row.effective_start_month !== '' ? Number(row.effective_start_month) : null,
      effective_end_month: row.effective_end_month !== '' ? Number(row.effective_end_month) : null,
      notes: row.notes || null,
    };

    try {
      setSavingRow(category.id);
      if (row.id) {
        await feeStudentFeeService.update(row.id, payload);
        showToast.success('Assignment updated');
      } else {
        const response = await feeStudentFeeService.create(payload);
        if (response?.data?.id) {
          setRowState((prev) => ({
            ...prev,
            [category.id]: {
              ...prev[category.id],
              id: response.data.id,
            },
          }));
        }
        showToast.success('Assignment created');
      }
      await loadAssignments(selectedClass, selectedStudent);
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Save failed';
      showToast.error(message);
    } finally {
      setSavingRow(null);
    }
  };

  const handleDeleteRow = async (category) => {
    const row = rowState[category.id];
    if (!row?.id) {
      setRowState((prev) => ({
        ...prev,
        [category.id]: {
          ...defaultRowState,
          status: 'opted_out',
        },
      }));
      return;
    }
    if (!window.confirm('Remove this optional fee assignment?')) {
      return;
    }
    try {
      setSavingRow(category.id);
      await feeStudentFeeService.delete(row.id);
      showToast.success('Assignment removed');
      await loadAssignments(selectedClass, selectedStudent);
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Delete failed';
      showToast.error(message);
    } finally {
      setSavingRow(null);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} drawerWidth={drawerWidth} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            // On mobile: always full width, no margin (sidebar overlays)
            // On desktop: adjust width and margin based on sidebar state
            ml: { xs: 0, md: `${0}px` },
            backgroundColor: 'background.default',
          }}
        >
          <Header onMenuClick={handleDrawerToggle} />
          <Box
            sx={{
              p: { xs: 2, sm: 3 },
              mt: '64px',
              minHeight: 'calc(100vh - 64px)',
            }}
          >
          <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, border: 1, borderColor: 'divider', mb: 3 }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  Assign Student Fees
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Opt students into optional fee categories such as transport or hostel facilities.
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap">
                <Autocomplete
                  options={classes}
                  getOptionLabel={(option) => option ? `Class ${option.name}${option.section ? ` - ${option.section}` : ''}` : ''}
                  value={classes.find((cls) => cls.id === selectedClass) || null}
                  onChange={(event, newValue) => {
                    handleClassChange(newValue ? newValue.id : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Class"
                      placeholder="Search and select class..."
                      size="small"
                    />
                  )}
                  sx={{ minWidth: { xs: '100%', sm: 200 }, flex: { xs: '1 1 auto', sm: '0 1 auto' } }}
                  ListboxProps={{
                    style: {
                      maxHeight: 200,
                    },
                  }}
                  noOptionsText="No classes found"
                />
                <Autocomplete
                  options={studentOptions}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    const rollNo = option.roll_number ? `${option.roll_number} - ` : '';
                    return `${rollNo}${option.full_name}`;
                  }}
                  value={studentOptions.find((student) => student.id === selectedStudent) || null}
                  onChange={(event, newValue) => {
                    setSelectedStudent(newValue ? newValue.id : '');
                  }}
                  disabled={!selectedClass}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Student"
                      placeholder="Search and select student..."
                      size="small"
                    />
                  )}
                  sx={{ minWidth: { xs: '100%', sm: 220 }, flex: { xs: '1 1 auto', sm: '0 1 auto' } }}
                  ListboxProps={{
                    style: {
                      maxHeight: 200,
                    },
                  }}
                  noOptionsText="No students found"
                />
              </Stack>
            </Stack>
          </Paper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Paper elevation={0} sx={{ borderRadius: 3, border: 1, borderColor: 'divider', p: { xs: 2, sm: 3 } }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : !selectedClass ? (
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                Select a class and student to manage optional fees.
              </Typography>
            ) : categories.length === 0 ? (
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                No student-level fee categories found. Create one under Fee Categories first.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: { xs: 2, sm: 2.5 },
                }}
              >
                {categories.map((category) => {
                  const row = rowState[category.id] || defaultRowState;
                  const statusOptions = [
                    { value: 'opted_in', label: 'Opted In' },
                    { value: 'opted_out', label: 'Opted Out' },
                  ];
                  const isOptedIn = row.status === 'opted_in';
                  return (
                    <Paper
                      key={category.id}
                      variant="outlined"
                      sx={{
                        p: { xs: 2, sm: 2.5 },
                        borderRadius: 2,
                        borderColor: 'divider',
                      }}
                    >
                      <Stack spacing={2}>
                        {/* Header: Category Name with Avatar */}
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar
                            alt={category.name}
                            sx={{
                              width: 48,
                              height: 48,
                              bgcolor: getAvatarColor(category.name),
                              color: '#ffffff',
                              fontWeight: 700,
                            }}
                          >
                            {getInitials(category.name)}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {category.name}
                            </Typography>
                            {category.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {category.description}
                              </Typography>
                            )}
                          </Box>
                          <Chip
                            label={isOptedIn ? 'Opted In' : 'Opted Out'}
                            color={isOptedIn ? 'success' : 'error'}
                            size="small"
                            variant="filled"
                          />
                        </Stack>

                        <Divider />

                        {/* Form Fields */}
                        <Stack spacing={2}>
                          {/* Status */}
                          <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                              value={row.status || 'opted_out'}
                              onChange={handleRowChange(category.id, 'status')}
                              label="Status"
                            >
                              {statusOptions.map((option) => {
                                // Disable "Opted In" if end month has passed
                                const currentMonth = new Date().getMonth() + 1;
                                const effectiveEndMonth = row.effective_end_month !== '' && row.effective_end_month !== null 
                                  ? Number(row.effective_end_month) 
                                  : null;
                                const isExpired = effectiveEndMonth !== null && effectiveEndMonth > 0 && currentMonth > effectiveEndMonth;
                                const isDisabled = option.value === 'opted_in' && isExpired;
                                
                                return (
                                  <MenuItem 
                                    key={option.value} 
                                    value={option.value}
                                    disabled={isDisabled}
                                  >
                                    {option.label}
                                    {isDisabled && ' (End month has passed)'}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>

                          {/* Override Amount */}
                          <TextField
                            type="number"
                            size="small"
                            fullWidth
                            label="Override Amount"
                            placeholder={
                              row.base_amount
                                ? `Base ₹${Number(row.base_amount).toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                  })}`
                                : 'No class amount set'
                            }
                            value={row.amount_override === '' ? '' : row.amount_override}
                            onChange={handleRowChange(category.id, 'amount_override')}
                            InputProps={{ inputProps: { min: 0, step: '0.01' } }}
                            helperText={row.base_amount ? `Base amount: ₹${Number(row.base_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : 'No base amount set'}
                          />

                          {/* Effective Months */}
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <FormControl size="small" fullWidth>
                              <InputLabel id={`start-month-${category.id}`}>Start Month</InputLabel>
                              <Select
                                labelId={`start-month-${category.id}`}
                                label="Start Month"
                                value={row.effective_start_month === '' ? '' : Number(row.effective_start_month)}
                                onChange={handleRowChange(category.id, 'effective_start_month')}
                              >
                                {MONTH_OPTIONS.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <FormControl size="small" fullWidth>
                              <InputLabel id={`end-month-${category.id}`}>End Month</InputLabel>
                              <Select
                                labelId={`end-month-${category.id}`}
                                label="End Month"
                                value={row.effective_end_month === '' ? '' : Number(row.effective_end_month)}
                                onChange={handleRowChange(category.id, 'effective_end_month')}
                              >
                                {MONTH_OPTIONS.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Stack>

                          {/* Notes */}
                          <TextField
                            size="small"
                            fullWidth
                            label="Notes"
                            multiline
                            minRows={2}
                            value={row.notes}
                            onChange={handleRowChange(category.id, 'notes')}
                          />
                        </Stack>

                        <Divider />

                        {/* Actions */}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="contained"
                            disabled={!selectedStudent || savingRow === category.id}
                            onClick={() => handleSaveRow(category)}
                            startIcon={savingRow === category.id ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                            fullWidth={isMobile}
                          >
                            {savingRow === category.id ? 'Saving...' : 'Save'}
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            disabled={!selectedStudent || savingRow === category.id}
                            onClick={() => handleDeleteRow(category)}
                            startIcon={<DeleteIcon />}
                            fullWidth={isMobile}
                          >
                            Remove
                          </Button>
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Paper>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default AssignStudentFeesPage;

